// VenueDashboardLoyaltyPage.jsx — CON-525 "Collect the Lees" cross-venue loyalty.
// The waiter/owner-facing dashboard tile. Runs on a WAITER'S PHONE mid-shift:
// big touch targets, any confirm in ≤ 10 seconds. Three tabs:
//   INBOX  — live stamp-request + redemption codes to confirm/verify (polls 10s)
//   SETUP  — manager/owner opt-in wizard + settings (auto-creates the offer row)
//   STATS  — stamps-here vs freebies-given fairness view + per-staff visibility
//
// The backend is DONE + live on this branch. This page only talks to it via:
//   GET  /api/venue-dashboard/:slug                                  (venue id + myRole)
//   GET  /api/me/venues/:id/loyalty/pending                          (inbox)
//   POST /api/me/venues/:id/loyalty/stamps/:reqId/confirm|reject
//   POST /api/me/venues/:id/loyalty/redemptions/:redId/verify|revoke
//   GET  /api/me/venues/:id/loyalty/program                          (setup)
//   PATCH/api/me/venues/:id/loyalty/program                          (save/opt-in)
//   GET  /api/me/venues/:id/loyalty/stats                            (stats)
//
// NEVER say "loyalty points" — it's stamps / Lees. 🦊 = the Lee mascot placeholder.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardNavBar from './DashboardNavBar';
import { getApiBaseUrl } from '../../config/region';

const ORANGE = '#E46E34';
const GOLD = '#d4a017';

// ── auth (same multi-key pattern as the Offers/Home pages) ────────────────────
function token() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') || ''
  );
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

// ── formatting helpers ────────────────────────────────────────────────────────
const fmtVnd = (v) => (v == null || v === '' ? '' : `${Number(v).toLocaleString()}₫`);
const fmtVndNum = (v) => (v == null || v === '' ? '' : Number(v).toLocaleString());

function fmtCountdown(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  if (ms <= 0) return 'expired';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// force a re-render every second so countdowns tick without refetching
function useTick(active) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setN((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
}

// ── tiny shared styles ────────────────────────────────────────────────────────
const S = {
  input: {
    width: '100%', boxSizing: 'border-box', background: 'white',
    border: '1px solid #d1d5db', borderRadius: 10, padding: '13px 12px',
    fontSize: 15, color: '#111827', outline: 'none',
  },
  label: { fontSize: 11.5, fontWeight: 700, color: '#6b7280', margin: '14px 0 5px' },
  card: {
    background: 'white', border: '1px solid #e5e7eb', borderRadius: 14,
    padding: 14, marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  chip: (bg, fg) => ({
    background: bg, color: fg, fontSize: 11, fontWeight: 700,
    padding: '3px 10px', borderRadius: 20, flexShrink: 0, whiteSpace: 'nowrap',
  }),
};

const PRESENCE_LABEL_KEY = {
  gps: 'presenceGps',
  qr: 'presenceQr',
  wifi: 'presenceWifi',
  manual: 'presenceManual',
};
const PRESENCE_LABEL_DEFAULT = {
  gps: '📍 At venue',
  qr: '📷 QR scan',
  wifi: '📶 On venue WiFi',
  manual: '✍️ Manual',
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function VenueDashboardLoyaltyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const base = getApiBaseUrl();

  const [venue, setVenue] = useState(null); // {id, role}
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('inbox'); // inbox | setup | stats

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, { headers: authHeaders() });
        const j = await r.json().catch(() => ({}));
        const v = j?.data || j?.venue || j;
        if (!v?.id) throw new Error(j?.message || t('loyalty.venueNotFoundNoAccess'));
        if (alive) setVenue({ id: v.id, role: j?.myRole || v.myRole || v.role || null });
      } catch (e) {
        if (alive) setErr(e.message);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [base, slug]);

  const canManage = venue && (venue.role === 'owner' || venue.role === 'manager');

  const TABS = [
    { key: 'inbox', label: t('loyalty.inbox'), icon: '📥' },
    { key: 'stats', label: t('loyalty.stats'), icon: '📊' },
    { key: 'setup', label: t('loyalty.setup'), icon: '⚙️', managerOnly: true },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div style={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', position: 'sticky', top: 0, zIndex: 100,
        background: 'linear-gradient(135deg, #f97316 0%, #fbbf24 100%)',
      }}>
        <button onClick={() => navigate(`/venue-dashboard/${slug}`)} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
          color: 'white', fontSize: 20, width: 36, height: 36, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>🦊 {t('loyalty.collectTheLees')}</span>
        <div style={{ width: 36 }} />
      </div>

      {/* tab strip */}
      {!loading && !err && (
        <div style={{
          display: 'flex', gap: 6, padding: '10px 14px 0', position: 'sticky', top: 64,
          zIndex: 90, background: '#f9fafb', maxWidth: 560, width: '100%', margin: '0 auto', boxSizing: 'border-box',
        }}>
          {TABS.filter((tb) => !tb.managerOnly || canManage).map((tb) => {
            const on = tab === tb.key;
            return (
              <button key={tb.key} onClick={() => setTab(tb.key)} style={{
                flex: 1, padding: '11px 0', borderRadius: '12px 12px 0 0', fontSize: 13.5,
                fontWeight: 800, cursor: 'pointer', border: 'none',
                background: on ? 'white' : 'transparent',
                color: on ? ORANGE : '#9ca3af',
                boxShadow: on ? '0 -1px 4px rgba(0,0,0,0.05)' : 'none',
              }}>
                <span style={{ marginRight: 5 }}>{tb.icon}</span>{tb.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ flex: 1, maxWidth: 560, width: '100%', margin: '0 auto', padding: '14px 14px 96px', boxSizing: 'border-box' }}>
        {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('loyalty.loading')}</div>}
        {err && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>
            {err}
          </div>
        )}

        {!loading && !err && venue && (
          <>
            {tab === 'inbox' && <InboxTab base={base} venueId={venue.id} role={venue.role} />}
            {tab === 'stats' && <StatsTab base={base} venueId={venue.id} />}
            {tab === 'setup' && canManage && <SetupTab base={base} venueId={venue.id} />}
          </>
        )}
      </div>

      <DashboardNavBar />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — INBOX
// ══════════════════════════════════════════════════════════════════════════════
function InboxTab({ base, venueId, role }) {
  const { t } = useTranslation();
  const canRevoke = role === 'owner' || role === 'manager';
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [confirmReq, setConfirmReq] = useState(null);     // stamp request being confirmed
  const [verifyRed, setVerifyRed] = useState(null);       // redemption being verified
  const timer = useRef(null);

  useTick(true); // countdowns

  const load = useCallback(async (silent) => {
    if (!silent) setFirstLoad(true);
    try {
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/pending`, { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!j?.success) throw new Error(j?.message || t('loyalty.couldNotLoadPending'));
      setData(j);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    }
    setFirstLoad(false);
  }, [base, venueId]);

  // initial + 10s poll while mounted
  useEffect(() => {
    load(false);
    timer.current = setInterval(() => load(true), 10000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [load]);

  const stampReqs = data?.stamp_requests || [];
  const redemptions = data?.redemptions || [];
  const minSpend = data?.min_spend_vnd;
  const cumulative = data?.cumulative;

  const removeReq = (id) => setData((d) => ({ ...d, stamp_requests: (d.stamp_requests || []).filter((x) => x.id !== id) }));
  const patchRedemption = (id, patch) => setData((d) => ({
    ...d,
    redemptions: (d.redemptions || []).map((x) => (x.id === id ? { ...x, ...patch } : x)),
  }));

  if (firstLoad && !data) return <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('loyalty.loadingInbox')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#9ca3af' }}>{t('loyalty.liveUpdatesEvery10s')}</span>
        <button onClick={() => load(false)} style={{
          background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px',
          fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer',
        }}>↻ {t('loyalty.refresh')}</button>
      </div>

      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: 12, fontSize: 12.5, marginBottom: 10 }}>
          {err}
        </div>
      )}

      {stampReqs.length === 0 && redemptions.length === 0 && !err && (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '48px 16px', fontSize: 14, lineHeight: 1.5 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🦊</div>
          {t('loyalty.noPendingActivity')}
        </div>
      )}

      {/* STAMP REQUESTS */}
      {stampReqs.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '6px 0 8px' }}>
          🦊 {t('loyalty.stampRequests')}
        </div>
      )}
      {stampReqs.map((req) => {
        const cd = fmtCountdown(req.expires_at);
        const expired = cd === 'expired';
        return (
          <div key={req.id} style={{ ...S.card, opacity: expired ? 0.55 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22, fontFamily: 'monospace', fontWeight: 800, color: ORANGE, letterSpacing: 1 }}>
                {req.otc_masked || '••••••'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {req.presence_source && (
                    <span style={S.chip('#eef2ff', '#4338ca')}>{t(`loyalty.${PRESENCE_LABEL_KEY[req.presence_source]}`, PRESENCE_LABEL_DEFAULT[req.presence_source]) || req.presence_source}</span>
                  )}
                  {cd && (
                    <span style={S.chip(expired ? '#fee2e2' : '#fef3c7', expired ? '#991b1b' : '#92400e')}>
                      {expired ? t('loyalty.expired') : `⏱ ${cd}`}
                    </span>
                  )}
                </div>
                {req.accumulated_vnd != null && (
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 4 }}>
                    {t('loyalty.accumulated')}: {fmtVnd(req.accumulated_vnd)}{minSpend ? ` / ${fmtVnd(minSpend)}` : ''}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button disabled={expired} onClick={() => setConfirmReq(req)} style={bigBtn(ORANGE, expired)}>
                {t('loyalty.confirm')}
              </button>
              <RejectButton base={base} venueId={venueId} reqId={req.id} onDone={() => removeReq(req.id)} />
            </div>
          </div>
        );
      })}

      {/* REDEMPTIONS */}
      {redemptions.length > 0 && (
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 8px' }}>
          🎁 {t('loyalty.freebieRedemptions')}
        </div>
      )}
      {redemptions.map((red) => {
        const cd = fmtCountdown(red.otc_expires_at);
        const expired = cd === 'expired';
        const redeemed = !!red._redeemed;
        const undoLeft = redeemed && red._revokeUntil ? fmtCountdown(red._revokeUntil) : null;
        const undoOver = redeemed && red._revokeUntil && (new Date(red._revokeUntil).getTime() <= Date.now());
        return (
          <div key={red.id} style={{ ...S.card, opacity: expired && !redeemed ? 0.55 : 1, borderColor: redeemed ? '#bbf7d0' : '#e5e7eb', background: redeemed ? '#f0fdf4' : 'white' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>🎁</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {red.freebie_title || red.free_item || t('loyalty.freeReward')}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  <span style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 800, color: '#111827', letterSpacing: 1 }}>{red.otc_masked || '••••••'}</span>
                  {cd && !redeemed && (
                    <span style={S.chip(expired ? '#fee2e2' : '#fef3c7', expired ? '#991b1b' : '#92400e')}>
                      {expired ? t('loyalty.expired') : `⏱ ${cd}`}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {!redeemed && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button disabled={expired} onClick={() => setVerifyRed(red)} style={bigBtn('#16a34a', expired)}>
                  {t('loyalty.verifyCode')}
                </button>
              </div>
            )}

            {redeemed && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#166534' }}>
                  ✅ {t('loyalty.redeemedHandOver', { item: red.free_item || red.freebie_title || t('loyalty.reward') })}
                </div>
                {!undoOver && (
                  canRevoke ? (
                    <UndoButton
                      base={base} venueId={venueId} redId={red.id} undoLeft={undoLeft}
                      onUndone={() => patchRedemption(red.id, { _redeemed: false, _revokeUntil: null })}
                    />
                  ) : (
                    <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>{t('loyalty.madeAMistakeAskManager')}</div>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* CONFIRM SHEET */}
      {confirmReq && (
        <ConfirmStampSheet
          base={base} venueId={venueId} req={confirmReq}
          minSpend={minSpend} cumulative={cumulative}
          onClose={() => setConfirmReq(null)}
          onDone={() => { removeReq(confirmReq.id); setConfirmReq(null); }}
        />
      )}

      {/* VERIFY REDEMPTION SHEET */}
      {verifyRed && (
        <VerifyRedemptionSheet
          base={base} venueId={venueId} red={verifyRed}
          onClose={() => setVerifyRed(null)}
          onDone={(revokeMin) => {
            patchRedemption(verifyRed.id, {
              _redeemed: true,
              _revokeUntil: new Date(Date.now() + (revokeMin || 5) * 60000).toISOString(),
            });
            setVerifyRed(null);
          }}
        />
      )}
    </div>
  );
}

// big touch-target button
function bigBtn(color, disabled) {
  return {
    flex: 1, background: disabled ? '#e5e7eb' : color, border: 'none', color: disabled ? '#9ca3af' : 'white',
    borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 800,
    cursor: disabled ? 'default' : 'pointer',
  };
}

// ── reject button + inline preset reasons ─────────────────────────────────────
function RejectButton({ base, venueId, reqId, onDone }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const REASONS = [t('loyalty.reasonNoPurchase'), t('loyalty.reasonWrongCustomer'), t('loyalty.reasonOther')];

  const reject = async (reason) => {
    setBusy(true);
    try {
      await fetch(`${base}/me/venues/${venueId}/loyalty/stamps/${reqId}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }),
      });
      onDone();
    } catch { /* even on failure, poll will reconcile */ onDone(); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{
        flex: 1, background: 'white', border: '1px solid #d1d5db', color: '#6b7280',
        borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer',
      }}>{t('loyalty.reject')}</button>
    );
  }
  return (
    <div style={{ flex: 2, display: 'flex', gap: 6 }}>
      {REASONS.map((r) => (
        <button key={r} disabled={busy} onClick={() => reject(r)} style={{
          flex: 1, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          borderRadius: 10, padding: '10px 4px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
        }}>{r}</button>
      ))}
    </div>
  );
}

// ── undo button (manager+) ────────────────────────────────────────────────────
function UndoButton({ base, venueId, redId, undoLeft, onUndone }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const undo = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/redemptions/${redId}/revoke`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({}),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 403) throw new Error(t('loyalty.onlyManagerCanUndo'));
      if (!r.ok || j?.success === false) throw new Error(j?.message || t('loyalty.undoFailed'));
      onUndone();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 8 }}>
      <button disabled={busy} onClick={undo} style={{
        background: 'white', border: '1px solid #d1d5db', color: '#6b7280',
        borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}>↩ {t('loyalty.undo')}{undoLeft ? ` (${undoLeft})` : ''}</button>
      {err && <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 5 }}>{err}</div>}
    </div>
  );
}

// ── OTC 6-digit input ─────────────────────────────────────────────────────────
function OtcInput({ value, onChange, autoFocus }) {
  return (
    <input
      autoFocus={autoFocus}
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      placeholder="••••••"
      style={{
        width: '100%', boxSizing: 'border-box', textAlign: 'center', letterSpacing: 12,
        fontSize: 30, fontWeight: 800, fontFamily: 'monospace', color: '#111827',
        border: '2px solid #d1d5db', borderRadius: 14, padding: '16px 12px', outline: 'none',
      }}
    />
  );
}

// ── bottom-sheet wrapper ──────────────────────────────────────────────────────
function Sheet({ children, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', width: '100%', maxWidth: 560, borderRadius: '20px 20px 0 0',
        padding: '18px 18px calc(24px + env(safe-area-inset-bottom))', boxSizing: 'border-box',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {children}
      </div>
    </div>
  );
}

// ── confirm stamp sheet ───────────────────────────────────────────────────────
function ConfirmStampSheet({ base, venueId, req, minSpend, cumulative, onClose, onDone }) {
  const { t } = useTranslation();
  const [otc, setOtc] = useState('');
  const [spend, setSpend] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null); // {result, ...}

  const ERR_MSG = {
    code_mismatch: t('loyalty.errCodeMismatch'),
    expired: t('loyalty.errCodeExpired'),
    under_threshold: t('loyalty.errUnderThreshold'),
    cooldown: t('loyalty.errCooldown'),
    daily_cap: t('loyalty.errDailyCap'),
  };

  const submit = async () => {
    if (otc.length !== 6) { setErr(t('loyalty.enter6DigitCode')); return; }
    setBusy(true); setErr(null);
    try {
      const body = { otc_code: otc };
      if (spend) body.spend_vnd = Number(String(spend).replace(/\D/g, ''));
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/stamps/${req.id}/confirm`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) {
        const code = j?.error || j?.code;
        throw new Error(ERR_MSG[code] || j?.message || t('loyalty.couldNotConfirmTryAgain'));
      }
      setOk(j); // {result:'stamp_issued'|'progress', ...}
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  // success state
  if (ok) {
    const gold = ok.tier === 'gold';
    if (ok.result === 'progress') {
      return (
        <Sheet onClose={onDone}>
          <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <div style={{ fontSize: 44 }}>💰</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#111827', marginTop: 6 }}>{t('loyalty.progressSaved')}</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 6, lineHeight: 1.5 }}>
              {ok.message || t('loyalty.moreToNextStamp', { amount: fmtVnd(ok.remaining_vnd) })}
              {ok.accumulated_vnd != null && (
                <div style={{ marginTop: 4 }}>{t('loyalty.nowAt', { amount: fmtVnd(ok.accumulated_vnd) })}</div>
              )}
            </div>
            <button onClick={onDone} style={{ ...bigBtn(ORANGE), marginTop: 18, width: '100%' }}>{t('loyalty.done')}</button>
          </div>
        </Sheet>
      );
    }
    // stamp_issued
    return (
      <Sheet onClose={onDone}>
        <div style={{
          textAlign: 'center', padding: '16px 0 8px', borderRadius: 16,
          background: gold ? 'linear-gradient(135deg, #fff7e0, #fde68a)' : 'transparent',
        }}>
          <div style={{ fontSize: 54 }}>🦊</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: gold ? '#92610a' : '#111827', marginTop: 4 }}>
            {t('loyalty.stampGiven')}
          </div>
          {ok.sticker?.label && (
            <div style={{ fontSize: 14, color: gold ? '#92610a' : '#6b7280', marginTop: 6 }}>
              {ok.sticker.artwork_url ? '' : '🏷️ '}{ok.sticker.label}
            </div>
          )}
          {ok.tier && (
            <div style={{ display: 'inline-block', marginTop: 10, ...S.chip(gold ? '#fef3c7' : '#eef2ff', gold ? '#92610a' : '#4338ca') }}>
              {gold ? `🌟 ${t('loyalty.goldTier')}` : t('loyalty.tierColon', { tier: ok.tier })}
            </div>
          )}
          {ok.sticker?.artwork_url && (
            <div style={{ marginTop: 12 }}>
              <img src={ok.sticker.artwork_url} alt={ok.sticker.label || 'Lee'} style={{ width: 96, height: 96, objectFit: 'contain' }} />
            </div>
          )}
          <button onClick={onDone} style={{ ...bigBtn(gold ? GOLD : ORANGE), marginTop: 18, width: '100%' }}>{t('loyalty.done')}</button>
        </div>
      </Sheet>
    );
  }

  // entry form
  return (
    <Sheet onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', flex: 1 }}>{t('loyalty.confirmStamp')}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 20, cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
        {t('loyalty.askCustomerForCode')} (<b style={{ fontFamily: 'monospace' }}>{t('loyalty.starts')} {req.otc_masked || '••••••'}</b>).
      </div>

      <div style={S.label}>{t('loyalty.customersCode')}</div>
      <OtcInput value={otc} onChange={setOtc} autoFocus />

      <div style={S.label}>{t('loyalty.spendOptional')}</div>
      <input
        inputMode="numeric"
        value={spend ? fmtVndNum(String(spend).replace(/\D/g, '')) : ''}
        onChange={(e) => setSpend(e.target.value.replace(/\D/g, ''))}
        placeholder={t('loyalty.spendPlaceholder')}
        style={S.input}
      />
      {minSpend != null && (
        <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6 }}>
          {cumulative
            ? t('loyalty.stampMinimumCumulative', { amount: fmtVnd(minSpend) })
            : t('loyalty.stampMinimum', { amount: fmtVnd(minSpend) })}
        </div>
      )}

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: 11, fontSize: 12.5, marginTop: 12 }}>{err}</div>}

      <button disabled={busy || otc.length !== 6} onClick={submit} style={{
        ...bigBtn(ORANGE, busy || otc.length !== 6), width: '100%', marginTop: 16,
      }}>
        {busy ? t('loyalty.confirming') : `🦊 ${t('loyalty.giveTheStamp')}`}
      </button>
    </Sheet>
  );
}

// ── verify redemption sheet ───────────────────────────────────────────────────
function VerifyRedemptionSheet({ base, venueId, red, onClose, onDone }) {
  const { t } = useTranslation();
  const [otc, setOtc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (otc.length !== 6) { setErr(t('loyalty.enter6DigitCode')); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/redemptions/${red.id}/verify`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ otc_token: otc }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false || j?.result !== 'redeemed') {
        const code = j?.error || j?.code;
        const M = {
          code_mismatch: t('loyalty.errCodeDoesntMatch'),
          expired: t('loyalty.errRedemptionExpired'),
        };
        throw new Error(M[code] || j?.message || t('loyalty.couldNotVerifyTryAgain'));
      }
      onDone(j.revoke_window_min);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <Sheet onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#111827', flex: 1 }}>{t('loyalty.verifyFreebie')}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 20, cursor: 'pointer' }}>✕</button>
      </div>
      <div style={{ fontSize: 13.5, color: '#374151', marginBottom: 12 }}>
        <b>{red.freebie_title || red.free_item || t('loyalty.freeReward')}</b><br />
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>{t('loyalty.typeCustomersCodeToHandOver')}</span>
      </div>
      <OtcInput value={otc} onChange={setOtc} autoFocus />
      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: 11, fontSize: 12.5, marginTop: 12 }}>{err}</div>}
      <button disabled={busy || otc.length !== 6} onClick={submit} style={{ ...bigBtn('#16a34a', busy || otc.length !== 6), width: '100%', marginTop: 16 }}>
        {busy ? t('loyalty.verifying') : `✅ ${t('loyalty.verifyAndRedeem')}`}
      </button>
    </Sheet>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — SETUP (manager/owner)
// ══════════════════════════════════════════════════════════════════════════════
function SetupTab({ base, venueId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/program`, { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!j?.success) throw new Error(j?.message || t('loyalty.couldNotLoadProgram'));
      setData(j);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [base, venueId, t]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('loyalty.loadingSetup')}</div>;
  if (err) return <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>{err}</div>;

  const menuItems = data?.menu_items || [];
  const effMin = data?.effective_min_spend_vnd;

  return data?.program
    ? <ProgramSettings base={base} venueId={venueId} program={data.program} menuItems={menuItems} effMin={effMin} onSaved={load} />
    : <OptInWizard base={base} venueId={venueId} menuItems={menuItems} effMin={effMin} onJoined={load} />;
}

// group menu items by section for pickers
function groupMenu(menuItems) {
  const map = {};
  (menuItems || []).forEach((it) => {
    const sec = it.section_name || 'Menu';
    (map[sec] = map[sec] || []).push(it);
  });
  return map;
}

// menu-item OR free-text freebie picker
function FreebiePicker({ menuItems, itemId, itemName, onPick }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [freeText, setFreeText] = useState(!itemId && !!itemName);
  const groups = groupMenu(menuItems);
  const filtered = (it) => !q || (it.item_name || '').toLowerCase().includes(q.toLowerCase());

  if (freeText || menuItems.length === 0) {
    return (
      <div>
        <input
          style={S.input}
          value={itemName || ''}
          onChange={(e) => onPick({ id: null, name: e.target.value })}
          placeholder={t('loyalty.freebieNamePlaceholder')}
          maxLength={80}
        />
        {menuItems.length > 0 && (
          <button onClick={() => { setFreeText(false); onPick({ id: null, name: '' }); }} style={linkBtn}>
            ← {t('loyalty.pickFromMenuInstead')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <input style={{ ...S.input, marginBottom: 8 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('loyalty.searchMenu')} />
      <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        {Object.entries(groups).map(([sec, items]) => {
          const vis = items.filter(filtered);
          if (!vis.length) return null;
          return (
            <div key={sec}>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 12px 4px', background: '#f9fafb' }}>{sec}</div>
              {vis.map((it) => {
                const on = itemId === it.id;
                return (
                  <button key={it.id} onClick={() => onPick({ id: it.id, name: it.item_name, price: it.item_price })} style={{
                    display: 'flex', width: '100%', alignItems: 'center', gap: 8, textAlign: 'left',
                    padding: '11px 12px', border: 'none', borderTop: '1px solid #f3f4f6',
                    background: on ? '#fff7ed' : 'white', cursor: 'pointer',
                  }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: on ? 700 : 500, color: '#111827' }}>{it.item_name}</span>
                    {it.item_price != null && <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtVnd(it.item_price)}</span>}
                    {on && <span style={{ color: ORANGE, fontWeight: 800 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <button onClick={() => { setFreeText(true); onPick({ id: null, name: '' }); }} style={linkBtn}>
        {t('loyalty.notOnMenuTypeIt')} →
      </button>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: ORANGE, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', padding: '8px 0 0' };

// ── opt-in wizard (3 steps) ───────────────────────────────────────────────────
function OptInWizard({ base, venueId, menuItems, effMin, onJoined }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [f, setF] = useState({
    mode: 'cross_venue',
    cumulative_spend: true,
    min_spend_vnd: '',
    freebie_menu_item_id: null,
    freebie_menu_item_name: '',
    freebie_price: null,
    own_card_stamps_required: 10,
    own_freebie_menu_item_id: null,
    own_freebie_menu_item_name: '',
    weekly_redemption_cap: 10,
    monthly_redemption_cap: '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const hasOwnCard = f.mode === 'both' || f.mode === 'own_venue';

  const canNext1 = !!(f.freebie_menu_item_id || (f.freebie_menu_item_name || '').trim());
  const exposure = f.freebie_price != null && f.weekly_redemption_cap
    ? Number(f.freebie_price) * Number(f.weekly_redemption_cap)
    : null;

  const join = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        active: true,
        mode: f.mode,
        stamp_policy: 'both',
        min_spend_vnd: f.min_spend_vnd ? Number(f.min_spend_vnd) : null,
        cumulative_spend: !!f.cumulative_spend,
        freebie_menu_item_id: f.freebie_menu_item_id || null,
        freebie_menu_item_name: f.freebie_menu_item_name || null,
        weekly_redemption_cap: f.weekly_redemption_cap ? Number(f.weekly_redemption_cap) : null,
        monthly_redemption_cap: f.monthly_redemption_cap ? Number(f.monthly_redemption_cap) : null,
      };
      if (hasOwnCard) {
        body.own_card_stamps_required = f.own_card_stamps_required ? Number(f.own_card_stamps_required) : 10;
        body.own_freebie_menu_item_id = f.own_freebie_menu_item_id || null;
        body.own_freebie_menu_item_name = f.own_freebie_menu_item_name || null;
      }
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/program`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.message || t('loyalty.couldNotJoinTryAgain'));
      onJoined();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div style={{ ...S.card, padding: 16 }}>
      {/* intro */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 36 }}>🦊</div>
        <div style={{ fontSize: 17, fontWeight: 900, color: '#111827' }}>{t('loyalty.joinCollectTheLees')}</div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>
          {t('loyalty.joinIntroText')}
        </div>
      </div>

      {/* step dots */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={{ width: 26, height: 5, borderRadius: 3, background: step >= n ? ORANGE : '#e5e7eb' }} />
        ))}
      </div>

      {/* STEP 1 — freebie */}
      {step === 1 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{t('loyalty.step1Freebie')}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', margin: '4px 0 10px', lineHeight: 1.5 }}>
            {t('loyalty.step1FreebieHelp')}
          </div>
          <FreebiePicker
            menuItems={menuItems}
            itemId={f.freebie_menu_item_id}
            itemName={f.freebie_menu_item_name}
            onPick={({ id, name, price }) => setF((p) => ({ ...p, freebie_menu_item_id: id, freebie_menu_item_name: name, freebie_price: price ?? null }))}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button disabled={!canNext1} onClick={() => setStep(2)} style={{ ...bigBtn(ORANGE, !canNext1), width: '100%' }}>{t('loyalty.next')} →</button>
          </div>
        </>
      )}

      {/* STEP 2 — stamp rules */}
      {step === 2 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{t('loyalty.step2StampRules')}</div>

          <div style={S.label}>{t('loyalty.minSpendForStampOptional')}</div>
          <input inputMode="numeric" style={S.input}
            value={f.min_spend_vnd ? fmtVndNum(String(f.min_spend_vnd).replace(/\D/g, '')) : ''}
            onChange={(e) => set('min_spend_vnd', e.target.value.replace(/\D/g, ''))}
            placeholder={effMin ? fmtVndNum(effMin) : t('loyalty.leaveEmptyAnyPurchase')} />
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>{t('loyalty.leaveEmptyAnyPurchaseEarnsStamp')}</div>

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={f.cumulative_spend} onChange={(e) => set('cumulative_spend', e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
            <span>
              <b style={{ fontSize: 13.5, color: '#111827' }}>{t('loyalty.addSmallerBillsUp')}</b>
              <span style={{ display: 'block', fontSize: 11.5, color: '#6b7280' }}>{t('loyalty.threeSmallerBillsHint')}</span>
            </span>
          </label>

          <div style={S.label}>{t('loyalty.whichCard')}</div>
          {[
            { v: 'cross_venue', t: t('loyalty.cardCityWide'), d: t('loyalty.cardCityWideHint') },
            { v: 'both', t: t('loyalty.cardCityWidePlusOwn'), d: t('loyalty.cardCityWidePlusOwnHint') },
            { v: 'own_venue', t: t('loyalty.cardOwnOnly'), d: t('loyalty.cardOwnOnlyHint') },
          ].map((o) => {
            const on = f.mode === o.v;
            return (
              <button key={o.v} onClick={() => set('mode', o.v)} style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 8,
                background: on ? '#fff7ed' : '#f9fafb', border: on ? `1px solid ${ORANGE}` : '1px solid #e5e7eb',
                borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
              }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#111827' }}>{on ? '● ' : '○ '}{o.t}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>{o.d}</span>
              </button>
            );
          })}

          {hasOwnCard && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 4 }}>
              <div style={S.label}>{t('loyalty.stampsToFillYourCard')}</div>
              <input inputMode="numeric" style={S.input} value={f.own_card_stamps_required}
                onChange={(e) => set('own_card_stamps_required', e.target.value.replace(/\D/g, '') || '')} />
              <div style={S.label}>{t('loyalty.yourCardsFreebie')}</div>
              <FreebiePicker
                menuItems={menuItems}
                itemId={f.own_freebie_menu_item_id}
                itemName={f.own_freebie_menu_item_name}
                onPick={({ id, name }) => setF((p) => ({ ...p, own_freebie_menu_item_id: id, own_freebie_menu_item_name: name }))}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={() => setStep(1)} style={{ flex: 1, background: 'white', border: '1px solid #d1d5db', color: '#374151', borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>← {t('loyalty.back')}</button>
            <button onClick={() => setStep(3)} style={{ ...bigBtn(ORANGE), flex: 2 }}>{t('loyalty.next')} →</button>
          </div>
        </>
      )}

      {/* STEP 3 — caps + summary */}
      {step === 3 && (
        <>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{t('loyalty.step3CapsGoLive')}</div>

          <div style={S.label}>{t('loyalty.maxFreebiesPerWeek')}</div>
          <input inputMode="numeric" style={S.input} value={f.weekly_redemption_cap}
            onChange={(e) => set('weekly_redemption_cap', e.target.value.replace(/\D/g, '') || '')} />

          <div style={S.label}>{t('loyalty.maxFreebiesPerMonthOptional')}</div>
          <input inputMode="numeric" style={S.input} value={f.monthly_redemption_cap}
            onChange={(e) => set('monthly_redemption_cap', e.target.value.replace(/\D/g, ''))} placeholder={t('loyalty.noMonthlyCap')} />

          {/* plain-money exposure line (required) */}
          <div style={{ background: 'linear-gradient(135deg, #fff7ed, #fef3c7)', border: '1px solid #fde68a', borderRadius: 12, padding: 14, marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#92610a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('loyalty.yourMaximumExposure')}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#78350f', marginTop: 4 }}>
              {exposure != null ? t('loyalty.approxPerWeek', { amount: fmtVnd(exposure) }) : t('loyalty.setLater')}
              <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}> {t('loyalty.inFreebies')}</span>
            </div>
            <div style={{ fontSize: 11.5, color: '#b45309', marginTop: 4 }}>
              {exposure != null
                ? t('loyalty.exposureBreakdown', { count: f.weekly_redemption_cap, price: fmtVnd(f.freebie_price) })
                : t('loyalty.pickFreebieToSeeWeeklyCost')}
            </div>
          </div>

          {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: 11, fontSize: 12.5, marginTop: 12 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button onClick={() => setStep(2)} style={{ flex: 1, background: 'white', border: '1px solid #d1d5db', color: '#374151', borderRadius: 12, padding: '14px 0', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>← {t('loyalty.back')}</button>
            <button disabled={busy} onClick={join} style={{ ...bigBtn(ORANGE, busy), flex: 2 }}>
              {busy ? t('loyalty.joining') : `${t('loyalty.joinTheLeeProgram')} 🦊`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── existing-program settings form ────────────────────────────────────────────
function ProgramSettings({ base, venueId, program, menuItems, effMin, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    active: program.active !== false,
    mode: program.mode || 'cross_venue',
    min_spend_vnd: program.min_spend_vnd ?? '',
    cumulative_spend: program.cumulative_spend !== false,
    freebie_menu_item_id: program.freebie_menu_item_id ?? null,
    freebie_menu_item_name: program.freebie_menu_item_name || '',
    freebie_price: program.freebie_price ?? null,
    own_card_stamps_required: program.own_card_stamps_required ?? 10,
    own_freebie_menu_item_id: program.own_freebie_menu_item_id ?? null,
    own_freebie_menu_item_name: program.own_freebie_menu_item_name || '',
    weekly_redemption_cap: program.weekly_redemption_cap ?? 10,
    monthly_redemption_cap: program.monthly_redemption_cap ?? '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const hasOwnCard = f.mode === 'both' || f.mode === 'own_venue';

  const patch = async (extra) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const body = {
        active: f.active,
        mode: f.mode,
        stamp_policy: 'both',
        min_spend_vnd: f.min_spend_vnd ? Number(f.min_spend_vnd) : null,
        cumulative_spend: !!f.cumulative_spend,
        freebie_menu_item_id: f.freebie_menu_item_id || null,
        freebie_menu_item_name: f.freebie_menu_item_name || null,
        weekly_redemption_cap: f.weekly_redemption_cap ? Number(f.weekly_redemption_cap) : null,
        monthly_redemption_cap: f.monthly_redemption_cap ? Number(f.monthly_redemption_cap) : null,
        ...(hasOwnCard ? {
          own_card_stamps_required: f.own_card_stamps_required ? Number(f.own_card_stamps_required) : 10,
          own_freebie_menu_item_id: f.own_freebie_menu_item_id || null,
          own_freebie_menu_item_name: f.own_freebie_menu_item_name || null,
        } : {}),
        ...extra,
      };
      const r = await fetch(`${base}/me/venues/${venueId}/loyalty/program`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.message || t('loyalty.saveFailedPeriod'));
      setMsg(t('loyalty.savedPeriod'));
      onSaved && onSaved();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const exposure = f.freebie_price != null && f.weekly_redemption_cap
    ? Number(f.freebie_price) * Number(f.weekly_redemption_cap) : null;

  return (
    <div>
      {/* active toggle */}
      <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: '#111827' }}>🦊 {t('loyalty.leeProgram')}</div>
          <div style={{ fontSize: 12, color: f.active ? '#166534' : '#9ca3af' }}>{f.active ? t('loyalty.activeCollectingStamps') : t('loyalty.paused')}</div>
        </div>
        <button onClick={() => { const nv = !f.active; set('active', nv); patch({ active: nv }); }} disabled={busy} style={{
          width: 56, height: 30, borderRadius: 16, border: 'none', cursor: 'pointer', position: 'relative',
          background: f.active ? '#16a34a' : '#d1d5db', transition: 'background 0.15s',
        }}>
          <span style={{
            position: 'absolute', top: 3, left: f.active ? 29 : 3, width: 24, height: 24, borderRadius: '50%',
            background: 'white', transition: 'left 0.15s',
          }} />
        </button>
      </div>

      <div style={{ ...S.card }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{t('loyalty.settings')}</div>

        <div style={S.label}>{t('loyalty.yourFreebie')}</div>
        <FreebiePicker
          menuItems={menuItems}
          itemId={f.freebie_menu_item_id}
          itemName={f.freebie_menu_item_name}
          onPick={({ id, name, price }) => setF((p) => ({ ...p, freebie_menu_item_id: id, freebie_menu_item_name: name, freebie_price: price ?? p.freebie_price }))}
        />

        <div style={S.label}>{t('loyalty.minSpendForStamp')}</div>
        <input inputMode="numeric" style={S.input}
          value={f.min_spend_vnd ? fmtVndNum(String(f.min_spend_vnd).replace(/\D/g, '')) : ''}
          onChange={(e) => set('min_spend_vnd', e.target.value.replace(/\D/g, ''))}
          placeholder={effMin ? fmtVndNum(effMin) : t('loyalty.anyPurchase')} />

        <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 0', cursor: 'pointer' }}>
          <input type="checkbox" checked={f.cumulative_spend} onChange={(e) => set('cumulative_spend', e.target.checked)} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 13, color: '#111827' }}>{t('loyalty.smallerBillsAddUp')}</span>
        </label>

        <div style={S.label}>{t('loyalty.whichCard')}</div>
        <select style={S.input} value={f.mode} onChange={(e) => set('mode', e.target.value)}>
          <option value="cross_venue">{t('loyalty.cardCityWide')}</option>
          <option value="both">{t('loyalty.cardCityWidePlusOwn')}</option>
          <option value="own_venue">{t('loyalty.cardOwnOnly')}</option>
        </select>

        {hasOwnCard && (
          <>
            <div style={S.label}>{t('loyalty.stampsToFillOwnCard')}</div>
            <input inputMode="numeric" style={S.input} value={f.own_card_stamps_required}
              onChange={(e) => set('own_card_stamps_required', e.target.value.replace(/\D/g, '') || '')} />
            <div style={S.label}>{t('loyalty.yourCardsFreebie')}</div>
            <FreebiePicker
              menuItems={menuItems}
              itemId={f.own_freebie_menu_item_id}
              itemName={f.own_freebie_menu_item_name}
              onPick={({ id, name }) => setF((p) => ({ ...p, own_freebie_menu_item_id: id, own_freebie_menu_item_name: name }))}
            />
          </>
        )}

        <div style={S.label}>{t('loyalty.maxFreebiesPerWeek')}</div>
        <input inputMode="numeric" style={S.input} value={f.weekly_redemption_cap}
          onChange={(e) => set('weekly_redemption_cap', e.target.value.replace(/\D/g, '') || '')} />

        <div style={S.label}>{t('loyalty.maxFreebiesPerMonthOptional')}</div>
        <input inputMode="numeric" style={S.input} value={f.monthly_redemption_cap}
          onChange={(e) => set('monthly_redemption_cap', e.target.value.replace(/\D/g, ''))} placeholder={t('loyalty.noMonthlyCap')} />

        {exposure != null && (
          <div style={{ fontSize: 12, color: '#92610a', marginTop: 10, fontWeight: 600 }}>
            {t('loyalty.maxExposureLine', { amount: fmtVnd(exposure), count: f.weekly_redemption_cap, price: fmtVnd(f.freebie_price) })}
          </div>
        )}

        {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: 11, fontSize: 12.5, marginTop: 12 }}>{err}</div>}
        {msg && <div style={{ color: '#166534', fontSize: 12.5, marginTop: 10, fontWeight: 700 }}>{msg}</div>}

        <button disabled={busy} onClick={() => patch({})} style={{ ...bigBtn(ORANGE, busy), width: '100%', marginTop: 16 }}>
          {busy ? t('loyalty.saving') : t('loyalty.saveChanges')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — STATS
// ══════════════════════════════════════════════════════════════════════════════
function StatsTab({ base, venueId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await fetch(`${base}/me/venues/${venueId}/loyalty/stats`, { headers: authHeaders() });
        const j = await r.json().catch(() => ({}));
        if (!j?.success) throw new Error(j?.message || t('loyalty.couldNotLoadStats'));
        if (alive) setData(j);
      } catch (e) { if (alive) setErr(e.message); }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, venueId]);

  if (loading) return <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('loyalty.loadingStats')}</div>;
  if (err) return <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>{err}</div>;

  const b = data?.balance || {};
  const perStaff = data?.per_staff || [];
  const recent = data?.recent_stamps || [];

  const Head = ({ big, label, hint, accent }) => (
    <div style={{ ...S.card, marginBottom: 0, textAlign: 'center', flex: 1 }}>
      <div style={{ fontSize: 26, fontWeight: 900, color: accent || '#111827' }}>{big ?? 0}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 2 }}>{label}</div>
      {hint && <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 2, lineHeight: 1.35 }}>{hint}</div>}
    </div>
  );

  return (
    <div>
      {/* fairness view: stamps here vs freebies given */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <Head big={b.stamps_earned_here} label={t('loyalty.statStampsEarnedHere')} accent={ORANGE} hint={t('loyalty.statVisitsYouRewarded')} />
        <Head big={b.freebies_given} label={t('loyalty.statFreebiesGiven')} accent="#16a34a" hint={t('loyalty.statInLast7Days', { count: b.given_last_7d ?? 0 })} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
        <Head big={b.visits_gained_after_freebie} label={t('loyalty.statVisitsAfterFreebie')} accent="#4338ca" hint={t('loyalty.statCustomersReturnedHint')} />
        <Head big={b.unique_loyal_visitors} label={t('loyalty.statLoyalVisitors')} hint={t('loyalty.statUniqueRegulars')} />
      </div>
      {b.reserved_now != null && (
        <div style={{ fontSize: 12, color: '#6b7280', margin: '2px 2px 14px' }}>
          🔒 {t('loyalty.reservedNowLine', { count: b.reserved_now })}
        </div>
      )}

      {/* per-staff (anti-collusion visibility) */}
      <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 8px' }}>
        {t('loyalty.whoConfirmsStamps')}
      </div>
      {perStaff.length === 0 ? (
        <div style={{ fontSize: 12.5, color: '#9ca3af', padding: '4px 2px 12px' }}>{t('loyalty.noStampsConfirmedYet')}</div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
          {perStaff.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderTop: i ? '1px solid #f3f4f6' : 'none' }}>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: '#111827', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.confirmed_by || t('loyalty.staff')}
              </span>
              <span style={{ fontSize: 13, fontWeight: 800, color: ORANGE }}>{s.stamps || 0}</span>
              {s.last_at && <span style={{ fontSize: 11, color: '#9ca3af' }}>{new Date(s.last_at).toLocaleDateString()}</span>}
            </div>
          ))}
        </div>
      )}

      {/* recent stamps with fraud flags */}
      <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 8px' }}>
        {t('loyalty.recentStamps')}
      </div>
      {recent.length === 0 ? (
        <div style={{ fontSize: 12.5, color: '#9ca3af', padding: '4px 2px' }}>{t('loyalty.nothingYet')}</div>
      ) : (
        recent.map((r) => {
          const flagged = Array.isArray(r.fraud_flags) && r.fraud_flags.length > 0;
          return (
            <div key={r.id} style={{
              ...S.card, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10,
              borderColor: flagged ? '#fecaca' : '#e5e7eb', background: flagged ? '#fef2f2' : 'white',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {r.tier && <span style={S.chip(r.tier === 'gold' ? '#fef3c7' : '#eef2ff', r.tier === 'gold' ? '#92610a' : '#4338ca')}>{r.tier}</span>}
                  {r.grade && <span style={S.chip('#f3f4f6', '#374151')}>{r.grade}</span>}
                  {r.spend_vnd != null && <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtVnd(r.spend_vnd)}</span>}
                </div>
                {r.created_at && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>{new Date(r.created_at).toLocaleString()}</div>}
                {flagged && (
                  <div style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700, marginTop: 3 }}>
                    ⚠️ {r.fraud_flags.join(', ')}{r.risk_score != null ? ` · risk ${r.risk_score}` : ''}
                  </div>
                )}
              </div>
              {r.status && <span style={S.chip('#f3f4f6', '#6b7280')}>{r.status}</span>}
            </div>
          );
        })
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
